import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Col,
  Row,
  Text,
} from '@cubeyou-team/hx-react-components';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import { RepositoryItemLoader } from '../../../ContentLoaders';
import RepositoryItem from '../../../RepositoryItem';
import { isEmpty } from '../../../../utils/misc';
import {
  addSelectedAudiences,
  loadMoreRepository,
  removeAllSelectedAudiences,
  removeSelectedAudience,
  sendEvent,
} from '../../../../actions';
import RepositoryItemExtended from '../../../RepositoryItemExtended';
import AudiencesToComparePanel from '../AudiencesToComparePanel';
import { RepositoryItemProvider } from '../../../RepositoryItem/providers/RepositoryItemProvider';

const FoldersContent = ({
  items,
  isItemLoaded,
  extended,
  disableAudiences,
  hideActions,
  onFolderClick,
  onItemDragToggle,
  actions,
}) => {
  const dispatch = useDispatch();
  const scrollParentRef = useRef();
  const [isAllChecked, setIsAllChecked] = useState(true);
  const selectedAudiences = useSelector(
    state => state.repository.selectedAudiences
  );
  const shouldCallLoadMore = useSelector(
    state => state.repository.shouldCallLoadMore
  );
  const repositoryFilter = useSelector(state => state.repository.filter);
  const audiencesToCompare = useSelector(
    state => state.repository.audiencesToCompare
  );
  const isInitialLoading = useSelector(state => state.repository.isLoading);
  const isNextPageLoading = useSelector(
    state => state.repository.isLoadingMore
  );
  const filteredItems = items.filter(
    item =>
      !item.metadata ||
      (item.metadata &&
        (!['conversation', 'tsv'].includes(item.metadata.audience_type) ||
          (['conversation', 'tsv'].includes(item.metadata.audience_type) &&
            item.metadata.status === 'ready')))
  );

  const filteredSelectedItems = selectedAudiences.filter(selected =>
    filteredItems.some(filtered => selected.id === filtered.id)
  );

  const loadMoreItems = useCallback(() => {
    if (isNextPageLoading || isInitialLoading) {
      return false;
    }
    dispatch(loadMoreRepository(repositoryFilter));
  }, [isNextPageLoading, isInitialLoading, dispatch, repositoryFilter]);

  const handleClearSelectionItems = useCallback(
    event => {
      dispatch(removeAllSelectedAudiences());
    },
    [dispatch]
  );

  useEffect(() => {
    window.addEventListener(
      'CLEAR_MULTIPLE_SELECTION_REPOSITORY_ITEMS',
      handleClearSelectionItems
    );
    return () => {
      window.removeEventListener(
        'CLEAR_MULTIPLE_SELECTION_REPOSITORY_ITEMS',
        handleClearSelectionItems
      );
    };
  }, [handleClearSelectionItems]);

  useEffect(() => {
    let allChecked = 'semi-checked';
    if (isEmpty(filteredSelectedItems) || isEmpty(filteredItems)) {
      allChecked = false;
    }

    if (
      !isEmpty(filteredItems) &&
      filteredSelectedItems.length === filteredItems.length
    ) {
      allChecked = true;
    }

    setIsAllChecked(allChecked);
  }, [filteredItems, filteredSelectedItems]);

  const handleOnSelectAllItems = useCallback(() => {
    const checked = !isAllChecked;
    setIsAllChecked(checked);

    if (!checked) {
      return dispatch(removeAllSelectedAudiences());
    }
    dispatch(
      addSelectedAudiences(
        filteredItems.map(item => ({
          ...item,
        }))
      )
    );
  }, [isAllChecked, filteredItems, dispatch]);

  const list = useMemo(() => {
    const renderItem = ({ index, style }) => {
      if (!isItemLoaded(index)) {
        return (
          <div style={style}>
            <RepositoryItemLoader />
          </div>
        );
      }

      const item = items[index];

      if (extended) {
        return (
          <RepositoryItemExtended
            style={style}
            key={`folder-navigation-item-${index}-${item.id}`}
            item={item}
            navigationDisabled={disableAudiences && item.type !== 'folder'}
            actions={actions}
            checked={filteredSelectedItems.some(s => s.id === item.id)}
            onSelect={() => {
              if (filteredSelectedItems.find(s => s.id === item.id)) {
                dispatch(removeSelectedAudience(item.id));
              } else {
                dispatch(addSelectedAudiences([{ ...item }]));
              }
            }}
            onFolderClick={onFolderClick}
          />
        );
      }

      return (
        <RepositoryItem
          style={style}
          key={`folder-navigation-item-${index}-${item.id}`}
          item={item}
          navigationDisabled={disableAudiences && item.type !== 'folder'}
          disabled={disableAudiences && item.type !== 'folder'}
          hideActions={hideActions}
          actions={actions}
          onFolderClick={onFolderClick}
          onItemDragToggle={onItemDragToggle}
        />
      );
    };

    return (
      <div
        style={{ height: '100%', overflow: 'auto' }}
        ref={ref => (scrollParentRef.current = ref)}
      >
        <RepositoryItemProvider>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMoreItems}
            hasMore={shouldCallLoadMore[repositoryFilter]}
            threshold={5}
            loader={
              <div key="loader">
                <RepositoryItemLoader />
              </div>
            }
            useWindow={false}
            getScrollParent={() => scrollParentRef.current}
          >
            {items.map((item, index) => (
              <div key={`list-item-${item.id}-${index}`}>
                {renderItem({ item, index })}
              </div>
            ))}
          </InfiniteScroll>
        </RepositoryItemProvider>
      </div>
    );
  }, [
    items,
    isItemLoaded,
    shouldCallLoadMore,
    loadMoreItems,
    disableAudiences,
    hideActions,
    actions,
    onFolderClick,
    onItemDragToggle,
    extended,
    filteredSelectedItems,
    dispatch,
    repositoryFilter,
  ]);

  if (!extended) {
    return list;
  }

  const nameColumn = repositoryFilter !== 'my' ? 'Audience name' : 'Name';
  const counterAndNameColumn =
    filteredSelectedItems.length > 0
      ? `(${filteredSelectedItems.length}) ${nameColumn}`
      : nameColumn;
  const containsOnlyDisabledItems = items.every(
    item =>
      item.metadata &&
      ['conversation', 'tsv'].includes(item.metadata.audience_type) &&
      item.metadata.status !== 'ready'
  );
  return (
    <>
      <Box
        pY="small"
        pX="small"
        borderPosition="bottom"
        borderSize="2px"
        style={{ height: '50px', display: 'flex', alignItems: 'center' }}
      >
        <div style={{ width: '50px' }}>
          <Checkbox
            disabled={!isEmpty(audiencesToCompare) || containsOnlyDisabledItems}
            checked={
              filteredSelectedItems.length > 0 &&
              filteredSelectedItems.length < filteredItems.length
                ? 'semi-checked'
                : isAllChecked
            }
            onChange={() => {
              handleOnSelectAllItems();
            }}
          />
        </div>
        <div style={{ width: 'calc(100% - 50px)' }}>
          <Row valign="middle" style={{ height: '100%' }}>
            <Col span={3} style={{ display: 'flex', alignItems: 'center' }}>
              <Text size="xbody" weight="bold">
                {counterAndNameColumn}
              </Text>
              {filteredSelectedItems.length > 0 && (
                <Button
                  icon="delete"
                  size="small"
                  variant="subtle"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    const type =
                      filteredSelectedItems && filteredSelectedItems.length > 1
                        ? 'elements'
                        : filteredSelectedItems[0].type;
                    actions.delete(filteredSelectedItems, type);
                    dispatch(sendEvent('Workspace'));
                  }}
                >
                  Delete
                </Button>
              )}
            </Col>
            <Col
              span={['conversation', 'tsv'].includes(repositoryFilter) ? 2 : 3}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Text size="xbody" weight="bold">
                Creation date
              </Text>
            </Col>
            {['conversation', 'tsv'].includes(repositoryFilter) && (
              <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                <Text size="xbody" weight="bold">
                  Status
                </Text>
              </Col>
            )}
          </Row>
        </div>
      </Box>
      {list}
      {!isEmpty(audiencesToCompare) && <AudiencesToComparePanel />}
    </>
  );
};

export default FoldersContent;
